import { useMemo } from 'react';
import type { SignupFlow } from 'corgi-types';

import type { GqlValidationRules } from 'common/components/DynamicForm/types';
import { adaptField, adaptFieldWithBlur } from 'common/components/DynamicForm/utils/field-adapter';
import PasswordField from 'common/components/Form/PasswordField';
import { InputWithReplace } from 'common/components/Input/StatefulInput/InputVariants';
import { CheckboxValue, HTMLAutoCompleteAttribute } from 'common/enums/form';
import { useLocalization } from 'common/providers/LocaleProvider';
import { Event } from 'common/types/auth';
import { FieldName } from '../enums';
import RememberMe from '../Form/RememberMe';

interface FormGroupsArgs {
  userFlow: SignupFlow;
  emailvalidationRules?: GqlValidationRules;
  withEmailField?: boolean;
  withForgotPasswordLink?: boolean;
  withAutoFocus?: boolean;
}

export const useFormGroups = ({
  userFlow,
  emailvalidationRules = null,
  withEmailField = true,
  withForgotPasswordLink = true,
  withAutoFocus = false,
}: FormGroupsArgs) => {
  const { t } = useLocalization();

  const formGroups = useMemo(
    () => [
      ...(withEmailField
        ? [
            {
              name: FieldName.EmailAddress,
              Component: InputWithReplace,
              ComponentProps: {
                type: 'email',
                autoComplete: HTMLAutoCompleteAttribute.Username,
                autoFocus: withAutoFocus,
                replace: (value: string) => value.toLowerCase(),
              },
              label: t('h24_login_form_email_address'),
              validation: {
                required: true,
                rules: emailvalidationRules,
              },
              initialValue: '',
            },
          ]
        : []),
      {
        name: FieldName.Password,
        Component: adaptFieldWithBlur(PasswordField),
        ComponentProps: {
          autoComplete: HTMLAutoCompleteAttribute.CurrentPassword,
          disableMessages: true,
          required: true,
        },
        label: t('h24_login_form_password'),
        validation: {
          rules: null,
          required: true,
          requiredErrorMessage: t('h24_password_required_error'),
        },
        initialValue: '',
      },
      {
        name: FieldName.RememberMe,
        Component: adaptField(RememberMe),
        ComponentProps: {
          labelText: t('h24_login_remember_me_label'),
          eventName: Event.LoginFormClick,
          withForgotPasswordLink,
          userFlow,
        },
        label: t('h24_login_remember_me_label'),
        validation: {
          rules: null,
          required: false,
        },
        initialValue: CheckboxValue.False,
      },
    ],
    [emailvalidationRules, withEmailField]
  );

  return formGroups;
};
